import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowBigUp, Lightbulb } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/MaskedReveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function SuggestionBoard() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ alias: "", title: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [voted, setVoted] = useState({});

  const load = () => api.get("/suggestions").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/suggestions", form);
      setForm({ alias: "", title: "", description: "" });
      toast.success("Idea posted — thanks for the inspiration!");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const vote = async (id) => {
    if (voted[id]) return;
    setVoted((v) => ({ ...v, [id]: true }));
    setItems((list) => list.map((s) => (s.id === id ? { ...s, votes: s.votes + 1 } : s)));
    try {
      await api.post(`/suggestions/${id}/vote`);
    } catch {
      /* optimistic */
    }
  };

  return (
    <section id="board" data-testid="board" className="relative py-28 md:py-40 bg-wine-surface/40 border-y border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Post form */}
          <div className="md:col-span-4">
            <div className="md:sticky md:top-28">
              <Reveal>
                <span className="text-xs uppercase tracking-[0.28em] text-gold">Members' ideas board</span>
                <h2 className="mt-5 font-display leading-[0.95] tracking-tight text-cream text-[clamp(2.2rem,4.5vw,3.4rem)]">
                  Got an idea for a <span className="italic text-terracotta">gathering?</span>
                </h2>
                <p className="mt-4 text-smoke leading-relaxed">
                  Post it under any alias you like. The most-loved ideas become our next
                  outings. No account needed.
                </p>
              </Reveal>

              <Reveal delay={0.1}>
                <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl border border-white/10 bg-wine-bg p-6">
                  <div>
                    <Label className="text-smoke">Your alias</Label>
                    <Input data-testid="sugg-alias" placeholder="e.g. Shiraz Sue" required value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} className="mt-1.5 h-11 bg-wine-surface border-white/15 text-cream" />
                  </div>
                  <div>
                    <Label className="text-smoke">Idea title</Label>
                    <Input data-testid="sugg-title" placeholder="e.g. Cheese & wine masterclass" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11 bg-wine-surface border-white/15 text-cream" />
                  </div>
                  <div>
                    <Label className="text-smoke">Tell us more</Label>
                    <Textarea data-testid="sugg-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-wine-surface border-white/15 text-cream" />
                  </div>
                  <button
                    data-testid="sugg-submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-terracotta py-3 text-sm font-semibold text-cream transition-all duration-300 hover:bg-gold hover:text-wine-bg disabled:opacity-60"
                  >
                    {submitting ? "Posting…" : "Post idea"}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>

          {/* Board */}
          <div className="md:col-span-8">
            {items.length === 0 ? (
              <div data-testid="board-empty" className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 text-center p-10">
                <Lightbulb className="h-8 w-8 text-gold" />
                <p className="mt-4 font-display text-2xl text-cream">Be the first to share an idea</p>
                <p className="mt-2 text-smoke">Your suggestion could become our next outing.</p>
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 gap-6 [column-fill:_balance]">
                {items.map((s, i) => (
                  <motion.div
                    key={s.id}
                    data-testid={`sugg-card-${i}`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                    className="mb-6 break-inside-avoid rounded-2xl border border-white/10 bg-wine-bg/70 backdrop-blur p-6 hover:border-gold/40 transition-colors duration-300"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-display text-xl md:text-2xl text-cream tracking-tight">{s.title}</h3>
                      <button
                        data-testid={`vote-btn-${i}`}
                        onClick={() => vote(s.id)}
                        className={`flex flex-col items-center rounded-xl border px-3 py-1.5 transition-all duration-300 ${
                          voted[s.id] ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-smoke hover:border-gold hover:text-gold"
                        }`}
                      >
                        <ArrowBigUp className="h-5 w-5" />
                        <span className="text-xs font-semibold" data-testid={`vote-count-${i}`}>{s.votes}</span>
                      </button>
                    </div>
                    <p className="mt-3 text-smoke leading-relaxed">{s.description}</p>
                    <p className="mt-4 text-xs uppercase tracking-[0.16em] text-terracotta">— {s.alias}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
